import LoungeQuestion from "../components/LoungeQuestion";
import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../LanguageContext";

const STATION_LINKS = {
  jed: "https://erp.alkhalejiah.com/survey_dash/JED",
  dmm: "https://erp.alkhalejiah.com/survey_dash/DMM",
  ruh: "https://erp.alkhalejiah.com/survey_dash/RUH",
};

const LoungeSurvey = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const stationLink = STATION_LINKS[(searchParams.get("station") || "").toLowerCase()] || null;
  const [pnr, setPnr] = useState("");
  const [pnrError, setPnrError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [surveyErrorCode, setSurveyErrorCode] = useState("");
  const [surveyType, setSurveyType] = useState("Lounge");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loungeToken, setLoungeToken] = useState(null); // ULID session token from verify-pnr; PII stays server-side
  // Language selection is now handled by the Header toggle, so PNR shows immediately.
  // Previously false — kept the language-selector screen as a gate before PNR entry.
  const [hasLanguageSelected, setHasLanguageSelected] = useState(true);
  const navigate = useNavigate();
  const { setLanguage, language } = useLanguage();
  const initialLanguageRef = useRef(null);
  const topCornerLanguageRef = useRef(null);

  // Update document direction and html class when language changes
  useEffect(() => {
    const currentLang = i18n.language || language || "en";
    document.documentElement.dir = currentLang === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = currentLang;
    const htmlElement = document.documentElement;
    if (currentLang === "ar") {
      htmlElement.classList.add("arabic");
    } else {
      htmlElement.classList.remove("arabic");
    }
  }, [i18n.language, language]);

  // Handle language change
  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    setLanguage(lang);
    if (!hasLanguageSelected) {
      setHasLanguageSelected(true);
    }
  };

  const handlePnrChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (value.length <= 6) {
      setPnr(value);
      setPnrError("");
    }
  };

  const validatePnr = () => {
    if (!pnr || pnr.length !== 6) {
      setPnrError(t("pnr_must_be_6_characters"));
      return false;
    }
    return true;
  };

  const verifyPnr = async () => {
    if (!validatePnr()) {
      return;
    }

    setIsVerifying(true);
    setPnrError("");

    try {
      const baseUrl = process.env.REACT_APP_BASE_URL || "/api";
      // Pass the user's chosen language so it's stored in the session token server-side
      const currentLang = i18n.language || language || "ar";
      const response = await axios.get(
        `${baseUrl}/Survey/verify-pnr?pnr=${pnr}&language=${currentLang}`
      );

      if (response.data.alreadyAnswered) {
        setPnrError(t("survey_answered_previously"));
      } else if (response.data.exists) {
        setIsVerified(true);
        // Store only the session token — no PII comes to the client
        setLoungeToken(response.data.token);
        await fetchQuestions();
      } else {
        setPnrError(t("pnr_not_found"));
      }
    } catch (error) {
      setPnrError(
        error.response?.data?.message || t("error_verifying_pnr")
      );
    } finally {
      setIsVerifying(false);
    }
  };

  const fetchQuestions = async () => {
    try {
      const baseUrl = process.env.REACT_APP_BASE_URL || "/api";
      const response = await axios.get(
        `${baseUrl}/Survey/lounge-questions`
      );
      // Initialize all questions with empty answers
      const initializedQuestions = response.data.map(q => ({
        ...q,
        answer: "",
        openAnswer: ""
      }));
      setQuestions(initializedQuestions);
    } catch (error) {
      setSurveyErrorCode(error.response?.data?.errorCode);
    }
  };

  const setQuestionAnswer = (questionId, answer, nextQuestionId, isAdding, openAnswer = "") => {
    setQuestions((prevQuestions) => {
      return prevQuestions.map((question) => {
        if (question.id !== questionId) return question;

        const isStarRatingMultiple = 
          question.type === "multipleChoice" &&
          question.options &&
          question.options.length === 5 &&
          question.options.every((opt, idx) => opt.value === (idx + 1).toString());

        let finalAnswer = answer;

        if (question.type === "multipleChoice" && !isStarRatingMultiple && question.answer) {
          // Regular multiple choice - allow multiple selections
          if (isAdding) {
            finalAnswer = question.answer + "," + answer;
          } else {
            let answers = question.answer.split(",");
            answers = answers.filter((a) => a !== answer);
            finalAnswer = answers.join(",");
          }
        } else if (isStarRatingMultiple) {
          // Star rating multiple choice - single selection only
          finalAnswer = answer;
        }

        // Check if it's a text question - handle various type formats
        // Type 2 = Text (enum: NumericOneChoice=0, MultipleChoice=1, Text=2, ...)
        const questionTypeNum = typeof question.type === 'number' ? question.type : parseInt(question.type);
        const isTextQuestion = 
          question.type === "text" || 
          question.type === "Text" || 
          questionTypeNum === 2 ||
          question.type === "2";
        
        if (isTextQuestion) {
          const expr = /(<([^>]+)>)/gi;
          const found = answer.match(expr);
          if (found && found.length > 0) {
            return {
              ...question,
              answer: "",
              nextQuestionId: nextQuestionId,
            };
          }
        }

        if (question.type === "oneChoiceWithOpenAnswer") {
          let optionHasText = question.options.find(
            (option) => option.value === answer
          )?.withText;
          if (optionHasText && !openAnswer) {
            return {
              ...question,
              answer: "",
              nextQuestionId: nextQuestionId,
            };
          }
        }

        return {
          ...question,
          answer: finalAnswer,
          nextQuestionId: nextQuestionId,
          openAnswer: openAnswer || question.openAnswer,
        };
      });
    });
  };

  const saveAllQuestions = async () => {
    setIsSubmitting(true);
    try {
      const baseUrl = process.env.REACT_APP_BASE_URL || "/api";
      
      // Debug: Log all questions before filtering
      console.log("All questions before filtering:", questions.map(q => ({
        id: q.id,
        order: q.order,
        type: q.type,
        answer: q.answer,
        hasAnswer: !!q.answer
      })));
      
      // Collect all answers from all questions
      // Include text questions even if empty (they can be submitted empty)
      const allAnswers = questions
        .filter(q => {
          // Check if it's a text question - handle various type formats
          // Type 2 = Text (enum: NumericOneChoice=0, MultipleChoice=1, Text=2, ...)
          const questionTypeNum = typeof q.type === 'number' ? q.type : parseInt(q.type);
          const isTextQuestion = 
            q.type === "text" || 
            q.type === "Text" || 
            questionTypeNum === 2 ||
            q.type === "2";
          
          // For text questions, always include (even if empty)
          if (isTextQuestion) {
            console.log(`Including text question ${q.order} (type: ${q.type}, typeNum: ${questionTypeNum}), answer: "${q.answer || ''}"`);
            return true;
          }
          
          // For other question types, only include if answer exists
          const hasAnswer = q.answer && q.answer.trim() !== "";
          if (!hasAnswer) {
            console.log(`Excluding question ${q.order} (type: ${q.type}, typeNum: ${questionTypeNum}) - no answer`);
          }
          return hasAnswer;
        })
        .map(question => ({
          questionId: question.id,
          answer: question.answer || "", // Ensure answer is always a string, even if empty
          openAnswer: question.openAnswer || "",
          questionOrder: question.order,
        }));
      
      console.log("Answers being sent:", allAnswers);

      const loungeAnswers = {
        token: loungeToken,   // session token carries all PII server-side
        answers: allAnswers,
      };

      await axios.post(
        `${baseUrl}/Survey/lounge-answers`,
        loungeAnswers
      );

      navigate(`/thank-you`);
    } catch (error) {
      console.error("Error saving survey:", error);
      // You might want to show an error message to the user here
    } finally {
      setIsSubmitting(false);
    }
  };

  // Language Selector Component
  const LanguageSelector = ({ isTopCorner = false }) => {
    const currentLang = i18n.language || language || "en";
    const isArabic = currentLang === "ar";
    const languageRef = isTopCorner ? topCornerLanguageRef : initialLanguageRef;

    return (
      <div
        ref={languageRef}
        className={`language-selector ${isTopCorner ? `language-selector-top ${isArabic ? "rtl" : "ltr"}` : ""}`}
        style={{
          top: isTopCorner ? "20px" : "auto",
        }}
      >
        <div
          className="language-selector-inline"
          style={{
            display: "flex",
            alignItems: "center",
            gap: isTopCorner ? "8px" : "12px",
            fontFamily: isArabic ? "flynas-Regular" : "GothamRounded-Medium",
            fontSize: isTopCorner ? "16px" : "22px",
            color: "#005159",
            direction: isArabic ? "rtl" : "ltr",
          }}
        >
          <button
            onClick={() => handleLanguageChange("ar")}
            className={`language-selector-inline-option ${currentLang === "ar" ? "active" : ""}`}
            style={{
              background: currentLang === "ar" && !isTopCorner ? "#f0f9f8" : "transparent",
              border: currentLang === "ar" && !isTopCorner ? "2px solid #00bfb3" : "none",
              cursor: "pointer",
              padding: isTopCorner ? "8px 12px" : "12px 20px",
              borderRadius: "8px",
              fontFamily: "flynas-Regular",
              fontSize: isTopCorner ? "16px" : "22px",
              color: currentLang === "ar" ? "#005159" : "#666",
              fontWeight: currentLang === "ar" ? "600" : "400",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
            onMouseEnter={(e) => {
              if (currentLang !== "ar") {
                e.currentTarget.style.color = "#005159";
                e.currentTarget.style.background = "#f0f9f8";
                if (!isTopCorner) {
                  e.currentTarget.style.border = "2px solid #00bfb3";
                }
              }
            }}
            onMouseLeave={(e) => {
              if (currentLang !== "ar") {
                e.currentTarget.style.color = "#666";
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.border = "none";
              }
            }}
          >
            <img src={`${process.env.PUBLIC_URL}/assets/images/ar.gif`} alt="Arabic" style={{ width: isTopCorner ? "18px" : "24px", height: "auto", borderRadius: "2px" }} />
            <span>{t("arabic")}</span>
          </button>
          <span style={{ color: "#ccc", fontSize: isTopCorner ? "18px" : "24px" }}>|</span>
          <button
            onClick={() => handleLanguageChange("en")}
            className={`language-selector-inline-option ${currentLang === "en" ? "active" : ""}`}
            style={{
              background: currentLang === "en" && !isTopCorner ? "#f0f9f8" : "transparent",
              border: currentLang === "en" && !isTopCorner ? "2px solid #00bfb3" : "none",
              cursor: "pointer",
              padding: isTopCorner ? "8px 12px" : "12px 20px",
              borderRadius: "8px",
              fontFamily: "GothamRounded-Medium",
              fontSize: isTopCorner ? "16px" : "22px",
              color: currentLang === "en" ? "#005159" : "#666",
              fontWeight: currentLang === "en" ? "600" : "400",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
            onMouseEnter={(e) => {
              if (currentLang !== "en") {
                e.currentTarget.style.color = "#005159";
                e.currentTarget.style.background = "#f0f9f8";
                if (!isTopCorner) {
                  e.currentTarget.style.border = "2px solid #00bfb3";
                }
              }
            }}
            onMouseLeave={(e) => {
              if (currentLang !== "en") {
                e.currentTarget.style.color = "#666";
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.border = "none";
              }
            }}
          >
            <img src={`${process.env.PUBLIC_URL}/assets/images/en.gif`} alt="English" style={{ width: isTopCorner ? "18px" : "24px", height: "auto", borderRadius: "2px" }} />
            <span>{t("english")}</span>
          </button>
        </div>
      </div>
    );
  };

  // Show language selector and PNR on same screen
  if (!isVerified) {
    // Bilingual welcome text
    const welcomeTextEn = "We value your feedback to enhance the flynas lounge experience";
    const welcomeTextAr = "نقدّر مشاركتك في تحسين تجربة صالات طيران ناس";
    const selectLanguageEn = "Select Language";
    const selectLanguageAr = "اختر اللغة";
    
    // Get current language
    const currentLang = i18n.language || language || "en";
    const isArabic = currentLang === "ar";

    return (
      <div className={i18n.language === "ar" ? "arabic" : ""}>
        <section className="section section-lg welcome">
          <div className="container">
            {/* Welcome Heading - Show only during language selection */}
            {!hasLanguageSelected && (
              <div className="bilingual-welcome-container">
                {/* Show only the text matching the currently selected language */}
                {isArabic ? (
                  <h1 className="bilingual-welcome-ar">
                    {welcomeTextAr}
                  </h1>
                ) : (
                  <h1 className="bilingual-welcome-en">
                    {welcomeTextEn}
                  </h1>
                )}
                {/* Previously showing both languages simultaneously — commented out in favour of single-language display
                <h1 className="bilingual-welcome-en">
                  {welcomeTextEn}
                </h1>
                <h1 className="bilingual-welcome-ar">
                  {welcomeTextAr}
                </h1>
                */}
              </div>
            )}
            
            {/* Show language selector if language not selected, otherwise show PNR input */}
            {!hasLanguageSelected ? (
              <>
                {/* Bottom language selector (Select Language / اختر اللغة) — commented out because the
                    Header now provides a persistent language toggle, making this redundant.
                <div className="bilingual-select-container">
                  <h2 className="bilingual-select-en">
                    {selectLanguageEn}
                  </h2>
                  <h2 className="bilingual-select-ar">
                    {selectLanguageAr}
                  </h2>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginTop: "20px",
                  }}
                >
                  <LanguageSelector />
                </div>
                */}
              </>
            ) : (
              <>
                {/* Welcome heading — single language based on Header toggle */}
                <div className="bilingual-welcome-container">
                  {isArabic ? (
                    <h1 className="bilingual-welcome-ar">{welcomeTextAr}</h1>
                  ) : (
                    <h1 className="bilingual-welcome-en">{welcomeTextEn}</h1>
                  )}
                </div>

                {/* Show PNR input after language selection */}
                <section className="section section-lg" style={{ marginTop: "30px" }}>
                  <div className="container rborder">
                    <div style={{ padding: "20px" }}>
                      <label htmlFor="pnr-input" style={{ display: "block", marginBottom: "15px", fontWeight: "bold", fontSize: "22px", textAlign: i18n.language === "ar" ? "right" : "left" }}>
                        {t("pnr_label")}
                      </label>
                      <div>
                        <input
                          id="pnr-input"
                          type="text"
                          value={pnr}
                          onChange={handlePnrChange}
                          onKeyPress={(e) => {
                            if (e.key === "Enter") {
                              verifyPnr();
                            }
                          }}
                          maxLength={6}
                          style={{
                            width: "100%",
                            padding: "12px",
                            fontSize: "18px",
                            textTransform: "uppercase",
                            letterSpacing: "2px",
                            border: pnrError ? "2px solid #dc3545" : "1px solid #ccc",
                            borderRadius: "4px",
                          }}
                          placeholder={t("pnr_placeholder")}
                          disabled={isVerifying}
                        />
                        {pnrError && (
                          <div style={{ color: "#dc3545", marginTop: "8px", fontSize: "14px" }}>
                            {pnrError}
                          </div>
                        )}
                      </div>
                      <button
                        className="btnSubmit"
                        onClick={verifyPnr}
                        disabled={isVerifying || !pnr || pnr.length !== 6}
                        style={{ marginTop: "20px", width: "100%" }}
                      >
                        {isVerifying ? t("verifying") : t("confirm")}
                      </button>
                      {stationLink && (
                        <div style={{ marginTop: "20px", textAlign: "center" }}>
                          <a
                            href={stationLink}
                            style={{
                              color: "#005159",
                              fontSize: "16px",
                              fontFamily: i18n.language === "ar" ? "flynas-Regular" : "GothamRounded-Medium",
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                          >
                            {i18n.language === "ar" ? "لست ضيف طيران ناس؟" : "Not a flynas guest?"}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </section>
              </>
            )}
          </div>
        </section>
        <br />
        <br />
      </div>
    );
  }

  return (
    <div className={i18n.language === "ar" ? "arabic" : ""}>
      <section className="section section-lg welcome">
        <div className="container">
          <h1>{t("thanks_for_choose_flynas")}</h1>
          <h2>{t("help_to_improve_experience")}</h2>
        </div>
      </section>
      {questions && questions.length > 0 && (
        <section className="section section-lg" style={{ padding: "25px 0" }}>
          <div className="container">
            <div style={{ 
              background: "#f8f9fa",
              borderRadius: "8px",
              padding: "18px 20px",
              marginBottom: "25px",
              border: "1px solid #e9ecef"
            }}>
              <div style={{ 
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: "20px",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "15px",
                color: "#495057",
                fontFamily: "GothamRounded-Book",
                direction: i18n.language === "ar" ? "rtl" : "ltr",
                textAlign: "center"
              }}>
                <span style={{ whiteSpace: "nowrap" }}>
                  ⭐⭐⭐⭐⭐ <strong>5</strong> – {t("rating_excellent")}
                </span>
                <span style={{ whiteSpace: "nowrap" }}>
                  ⭐⭐⭐⭐ <strong>4</strong> – {t("rating_good")}
                </span>
                <span style={{ whiteSpace: "nowrap" }}>
                  ⭐⭐⭐ <strong>3</strong> – {t("rating_acceptable")}
                </span>
                <span style={{ whiteSpace: "nowrap" }}>
                  ⭐⭐ <strong>2</strong> – {t("rating_poor")}
                </span>
                <span style={{ whiteSpace: "nowrap" }}>
                  ⭐ <strong>1</strong> – {t("rating_very_poor")}
                </span>
              </div>
            </div>
          </div>
        </section>
      )}
      {questions && questions.length > 0 ? (
        <>
          {questions.map((question) => {
            // Create a wrapper function to pass questionId to setQuestionAnswer
            const handleSetAnswer = (answer, nextQuestionId, isAdding = false, openAnswer = "") => {
              setQuestionAnswer(question.id, answer, nextQuestionId, isAdding, openAnswer);
            };

            return (
              <LoungeQuestion
                key={question.id}
                question={question}
                setQuestionAnswer={handleSetAnswer}
              />
            );
          })}
          <div style={{ margin: "30px 0" }}>
            <button
              className="btnSubmit"
              onClick={saveAllQuestions}
              disabled={isSubmitting}
              style={{ minWidth: "200px" }}
            >
              {isSubmitting ? t("submitting") || "Submitting..." : t("submit")}
            </button>
          </div>
        </>
      ) : surveyErrorCode && surveyErrorCode === "11" ? (
        <h3>{t("survey_answered_previously")}</h3>
      ) : (
        <h3>{t("load_survey")} .....</h3>
      )}
      <br />
      <br />
    </div>
  );
};

export default LoungeSurvey;

